import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { ArrowLeft, CreditCard, Loader2, Lock, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import SiteNav from "@/components/landing/Nav";
import { useCart } from "@/context/CartContext";
import { api, formatApiError } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

let stripePromise = null;
function getStripe(key) {
  if (!stripePromise) stripePromise = loadStripe(key);
  return stripePromise;
}

const appearance = {
  theme: "night",
  variables: {
    colorPrimary: "#D4AF37",
    colorBackground: "#13151A",
    colorText: "#FAF8F5",
    colorTextSecondary: "#9CA3AF",
    colorDanger: "#F87171",
    fontFamily: "Outfit, sans-serif",
    borderRadius: "10px",
  },
  rules: {
    ".Input": { border: "1px solid rgba(212,175,55,0.25)", backgroundColor: "#0B0C0E" },
    ".Input:focus": { border: "1px solid #D4AF37", boxShadow: "0 0 0 3px rgba(212,175,55,0.15)" },
    ".Label": { color: "#9CA3AF", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.12em" },
    ".Tab": { border: "1px solid rgba(212,175,55,0.25)", backgroundColor: "#0B0C0E" },
    ".Tab--selected": { border: "1px solid #D4AF37", backgroundColor: "rgba(212,175,55,0.08)" },
  },
};

function PaymentForm({ intent, customer, onPaid }) {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");

  const pay = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setPaying(true);
    setError("");
    const { error: err, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
      confirmParams: {
        return_url: `${window.location.origin}/checkout`,
        payment_method_data: {
          billing_details: { name: customer.name, email: customer.email, phone: customer.phone || undefined },
        },
      },
    });
    if (err) {
      setError(err.message || "Payment failed. Please try another card.");
      setPaying(false);
      return;
    }
    if (paymentIntent && paymentIntent.status === "succeeded") {
      await onPaid(paymentIntent.id);
    } else {
      setError("Your payment is processing. You'll be updated shortly.");
      setPaying(false);
    }
  };

  return (
    <form onSubmit={pay} data-testid="payment-form">
      <PaymentElement options={{ layout: "tabs" }} />
      {error && (
        <p data-testid="payment-error" className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{error}</p>
      )}
      <Button data-testid="pay-button" type="submit" disabled={!stripe || paying}
        className="mt-6 w-full rounded-full bg-gold py-6 text-base font-semibold text-ink transition-all duration-300 hover:bg-gold-hover hover:shadow-[0_0_30px_rgba(212,175,55,0.3)]">
        {paying ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Lock className="mr-2 h-4 w-4" /> Pay ${intent.amount.toFixed(2)}</>}
      </Button>
      <p className="mt-4 flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <CreditCard className="h-3.5 w-3.5 text-gold" /> Secure payment processed by Stripe — your card details never touch our servers
      </p>
    </form>
  );
}

export default function CheckoutPage() {
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "" });
  const [intent, setIntent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [returnProcessing, setReturnProcessing] = useState(false);
  const [attendeeNames, setAttendeeNames] = useState([]);
  const [stripeKey, setStripeKey] = useState(null);
  const [payMode, setPayMode] = useState("test");
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [shipping, setShipping] = useState({ address1: "", address2: "", city: "", state: "", zip: "" });
  const [productMap, setProductMap] = useState({});

  useEffect(() => {
    api.get("/products")
      .then(({ data }) => {
        const m = {};
        data.forEach((p) => { m[p.id] = p; });
        setProductMap(m);
      })
      .catch(() => {});
  }, []);

  const needsShipping = items.some((i) => {
    if (i.type !== "product") return false;
    const p = productMap[i.ref_id];
    if (!p) return true;
    return p.requires_shipping ?? !(p.variations || []).length;
  });

  const discount = promo?.discount || 0;
  const estimatedTotal = Math.max(0, subtotal - discount);
  const eventTicketTotal = items.filter((i) => i.type === "event").reduce((s, i) => s + i.quantity, 0);

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoLoading(true);
    try {
      const { data } = await api.post("/promos/validate", {
        code: promoInput.trim(),
        items: items.map((i) => ({
          type: i.type, ref_id: i.ref_id, quantity: i.quantity,
          size: i.size || null, variation_id: i.variation_id || null,
        })),
      });
      setPromo(data);
      toast.success(data.discount > 0 ? `Promo applied — ${data.description}.` : `${data.description} — doesn't apply to items in your cart.`);
    } catch (err) {
      setPromo(null);
      toast.error(formatApiError(err));
    } finally {
      setPromoLoading(false);
    }
  };

  useEffect(() => {
    api.get("/config/payments")
      .then(({ data }) => {
        setStripeKey(data.publishable_key);
        setPayMode(data.mode || "test");
      })
      .catch(() => setStripeKey(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY));
  }, []);

  // Handle bank/3DS redirect return
  useEffect(() => {
    const pi = params.get("payment_intent");
    const status = params.get("redirect_status");
    if (pi && status === "succeeded" && !returnProcessing) {
      setReturnProcessing(true);
      api.post("/checkout/confirm", { payment_intent_id: pi })
        .then(({ data }) => { clear(); navigate(`/order-confirmation/${data.order.id}`); })
        .catch((err) => toast.error(formatApiError(err)))
        .finally(() => setReturnProcessing(false));
    }
  }, [params, navigate, clear, returnProcessing]);

  const createIntent = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let nameCursor = 0;
      const { data } = await api.post("/checkout/intent", {
        items: items.map((i) => {
          let names = null;
          if (i.type === "event") {
            names = [customer.name, ...attendeeNames.slice(nameCursor, nameCursor + i.quantity - 1)].filter(Boolean).slice(0, i.quantity);
            nameCursor += i.quantity - 1;
          }
          return {
            type: i.type, ref_id: i.ref_id, quantity: i.quantity,
            size: i.size || null, variation_id: i.variation_id || null,
            registrant: i.registrant || null,
            attendees: names,
          };
        }),
        customer,
        promo_code: promo?.code || null,
        shipping: needsShipping ? shipping : null,
      });
      if (data.free) {
        clear();
        toast.success("Order complete — no payment needed!");
        navigate(`/order-confirmation/${data.order_id}`);
        return;
      }
      setIntent(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const onPaid = async (paymentIntentId) => {
    try {
      const { data } = await api.post("/checkout/confirm", { payment_intent_id: paymentIntentId });
      clear();
      navigate(`/order-confirmation/${data.order.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="checkout-page" className="min-h-screen bg-ink text-foreground">
      <Seo noindex title="Checkout — AB Hunter Consulting USA" siteName="AB Hunter Consulting USA" description="Secure checkout." />
      <SiteNav />
      <div className="lg:pl-64">
        <main className="hero-glow grain relative mx-auto max-w-6xl px-5 pb-24 pt-24 sm:px-8 lg:pt-16">
          <Link to="/" data-testid="checkout-back-link" className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-gold">
            <ArrowLeft className="h-4 w-4" /> Continue shopping
          </Link>
          <h1 className="mt-6 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
            {intent ? "Payment" : "Checkout"}
          </h1>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.25em] text-gold">
            {intent ? "Step 2 of 2 — Secure payment" : "Step 1 of 2 — Your details"}
          </p>

          {items.length === 0 && !intent ? (
            <div className="mt-16 flex flex-col items-center rounded-3xl border border-gold/15 bg-ink-card/60 py-20 text-center" data-testid="checkout-empty">
              <ShoppingBag className="h-12 w-12 text-gold/30" />
              <p className="mt-4 font-display text-xl">Your cart is empty</p>
              <p className="mt-2 text-sm text-muted-foreground">Add a product or event registration to check out.</p>
              <Button data-testid="checkout-empty-shop-button" asChild className="mt-7 rounded-full bg-gold px-8 font-semibold text-ink hover:bg-gold-hover">
                <Link to="/">Back to the site</Link>
              </Button>
            </div>
          ) : (
            <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:gap-14">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
                {!intent ? (
                  <form onSubmit={createIntent} data-testid="checkout-details-form" className="rounded-3xl border border-gold/20 bg-ink-card/70 p-7 sm:p-9">
                    <h2 className="font-display text-xl font-semibold">Contact & billing details</h2>
                    <div className="mt-6 space-y-4">
                      <div>
                        <Label htmlFor="co-name">Full name</Label>
                        <Input id="co-name" data-testid="checkout-name-input" required value={customer.name}
                          onChange={(e) => setCustomer({ ...customer, name: e.target.value })} className="mt-1.5 border-gold/20 bg-ink" />
                      </div>
                      <div>
                        <Label htmlFor="co-email">Email</Label>
                        <Input id="co-email" data-testid="checkout-email-input" type="email" required value={customer.email}
                          onChange={(e) => setCustomer({ ...customer, email: e.target.value })} className="mt-1.5 border-gold/20 bg-ink" />
                      </div>
                      <div>
                        <Label htmlFor="co-phone">Phone *</Label>
                        <Input id="co-phone" data-testid="checkout-phone-input" type="tel" required value={customer.phone}
                          onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} className="mt-1.5 border-gold/20 bg-ink" />
                      </div>
                      {needsShipping && (
                        <div className="rounded-2xl border border-gold/20 bg-ink p-5" data-testid="shipping-section">
                          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Shipping address</p>
                          <div className="mt-4 space-y-4">
                            <div>
                              <Label htmlFor="ship-address1">Street address</Label>
                              <Input id="ship-address1" data-testid="shipping-address1-input" required value={shipping.address1}
                                onChange={(e) => setShipping({ ...shipping, address1: e.target.value })} className="mt-1.5 border-gold/20 bg-ink" placeholder="123 Main St" />
                            </div>
                            <div>
                              <Label htmlFor="ship-address2">Apt / Suite (optional)</Label>
                              <Input id="ship-address2" data-testid="shipping-address2-input" value={shipping.address2}
                                onChange={(e) => setShipping({ ...shipping, address2: e.target.value })} className="mt-1.5 border-gold/20 bg-ink" />
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                              <div>
                                <Label htmlFor="ship-city">City</Label>
                                <Input id="ship-city" data-testid="shipping-city-input" required value={shipping.city}
                                  onChange={(e) => setShipping({ ...shipping, city: e.target.value })} className="mt-1.5 border-gold/20 bg-ink" />
                              </div>
                              <div>
                                <Label htmlFor="ship-state">State</Label>
                                <Input id="ship-state" data-testid="shipping-state-input" required value={shipping.state}
                                  onChange={(e) => setShipping({ ...shipping, state: e.target.value })} className="mt-1.5 border-gold/20 bg-ink" placeholder="LA" />
                              </div>
                              <div>
                                <Label htmlFor="ship-zip">ZIP</Label>
                                <Input id="ship-zip" data-testid="shipping-zip-input" required value={shipping.zip}
                                  onChange={(e) => setShipping({ ...shipping, zip: e.target.value })} className="mt-1.5 border-gold/20 bg-ink" />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {eventTicketTotal > 1 && (
                        <div className="rounded-2xl border border-gold/20 bg-ink p-5" data-testid="attendee-details">
                          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Attendee names</p>
                          <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                            You (<span className="text-foreground">{customer.name || "primary purchaser"}</span>) hold ticket 1 — your name, email, and phone are all we need. Just add a name for each additional ticket so everyone gets their own QR code at the door.
                          </p>
                          <div className="mt-4 space-y-3">
                            {Array.from({ length: eventTicketTotal - 1 }).map((_, k) => (
                              <Input
                                key={k}
                                data-testid={`attendee-name-${k + 2}`}
                                placeholder={`Ticket ${k + 2} — attendee name`}
                                value={attendeeNames[k] || ""}
                                onChange={(e) =>
                                  setAttendeeNames((prev) => {
                                    const arr = [...prev];
                                    arr[k] = e.target.value;
                                    return arr;
                                  })
                                }
                                className="border-gold/20 bg-ink"
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      <Button data-testid="checkout-continue-button" type="submit" disabled={loading}
                        className="w-full rounded-full bg-gold py-6 font-semibold text-ink transition-all hover:bg-gold-hover">
                        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : estimatedTotal <= 0 ? "Complete Free Order" : "Continue to Payment"}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="rounded-3xl border border-gold/20 bg-ink-card/70 p-7 sm:p-9" data-testid="payment-step">
                    <div className="flex items-center justify-between">
                      <h2 className="font-display text-xl font-semibold">Card details</h2>
                      <button data-testid="edit-details-button" onClick={() => setIntent(null)}
                        className="text-xs text-muted-foreground underline-offset-2 transition-colors hover:text-gold hover:underline">
                        Edit details
                      </button>
                    </div>
                    <p className="mt-1.5 text-sm text-muted-foreground">{customer.name} · {customer.email}</p>
                    <div className="mt-6">
                      {payMode === "live" && (
                        <p className="mb-4 rounded-lg border border-emerald-400/30 bg-emerald-400/10 px-4 py-2 text-xs font-medium text-emerald-300" data-testid="live-mode-badge">
                          Live payments are on — real cards will be charged.
                        </p>
                      )}
                      {stripeKey ? (
                        <Elements stripe={getStripe(stripeKey)} options={{ clientSecret: intent.client_secret, appearance }}>
                          <PaymentForm intent={intent} customer={customer} onPaid={onPaid} />
                        </Elements>
                      ) : (
                        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
                      )}
                    </div>
                  </div>
                )}
              </motion.div>

              <motion.aside
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.12, ease: EASE }}
                className="h-fit rounded-3xl border border-gold/20 bg-ink-card/70 p-7 sm:p-9"
                data-testid="order-summary"
              >
                <h2 className="font-display text-xl font-semibold">Order summary</h2>
                <div className="mt-6 space-y-4">
                  {items.map((item, i) => (
                    <div key={i} data-testid={`summary-item-${i}`} className="flex items-center gap-4">
                      <img src={item.image || "/assets/logo.png"} alt={item.name}
                        className="h-14 w-14 rounded-lg border border-gold/15 object-cover" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold">{item.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {item.variation_name ? `${item.variation_name} · ` : ""}{item.type === "event" ? `${item.quantity} attendee${item.quantity > 1 ? "s" : ""}` : `Qty ${item.quantity}${item.size ? ` · ${item.size}` : ""}`}
                        </p>
                      </div>
                      <span className="font-display text-sm font-semibold text-gold">${(item.unit_price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 space-y-2 border-t border-gold/15 pt-5 text-sm">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Subtotal</span><span>${subtotal.toFixed(2)}</span>
                  </div>
                  {promo && promo.discount > 0 && (
                    <div className="flex justify-between text-emerald-300" data-testid="promo-discount-line">
                      <span>Promo {promo.code}</span><span>−${promo.discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-muted-foreground">
                    <span>Processing</span><span>Included</span>
                  </div>
                  <div className="flex justify-between border-t border-gold/15 pt-3">
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Total</span>
                    <span data-testid="summary-total" className="font-display text-2xl font-semibold text-gold">
                      ${(intent ? intent.amount : estimatedTotal).toFixed(2)}
                    </span>
                  </div>
                </div>
                {!intent && (
                  <div className="mt-5 border-t border-gold/15 pt-5">
                    <Label htmlFor="promo-code">Promo code</Label>
                    <div className="mt-1.5 flex gap-2">
                      <Input id="promo-code" data-testid="promo-code-input" value={promoInput} disabled={!!promo}
                        onChange={(e) => setPromoInput(e.target.value)} placeholder="e.g. EARLYBIRD20"
                        className="border-gold/20 bg-ink uppercase placeholder:normal-case" />
                      {promo ? (
                        <Button type="button" data-testid="promo-remove-button" variant="outline" onClick={() => { setPromo(null); setPromoInput(""); }}
                          className="shrink-0 border-gold/30 bg-transparent text-gold hover:bg-gold/10">Remove</Button>
                      ) : (
                        <Button type="button" data-testid="promo-apply-button" variant="outline" onClick={applyPromo} disabled={promoLoading || !promoInput.trim()}
                          className="shrink-0 border-gold/30 bg-transparent text-gold hover:bg-gold/10">
                          {promoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
                        </Button>
                      )}
                    </div>
                    {promo && <p className="mt-1.5 text-xs text-emerald-300" data-testid="promo-applied-note">{promo.description} applied.</p>}
                  </div>
                )}
              </motion.aside>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
